import './RoasValue.css';

// Presentation only: the displayed figure remains unchanged, as do all targets,
// sorting, payout rules, and campaign status calculations.
export const hasDarkMatter = value => {
  if (typeof value !== 'number' && typeof value !== 'string') return false;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) && number >= 2;
};

export function RoasAward({ value, winRoas = Infinity }) {
  const diamond = hasDarkMatter(value);
  const winner = (typeof value === 'number' || typeof value === 'string') && Number.isFinite(Number(value)) && Number(value) >= winRoas;
  if (!diamond && !winner) return null;
  return <span className={`roas-award${diamond ? ' roas-award--diamond' : ''}`} role="img" aria-label={diamond ? 'Diamond ROAS: 2x or higher' : 'Winning ROAS'}>{diamond ? (
    <svg className="roas-award__stone" aria-hidden="true" viewBox="0 0 24 22" fill="none">
      <path d="M5 2h14l4 6-11 13L1 8Z" fill="#c9e6ff" stroke="#dff5ff" strokeWidth=".65" strokeLinejoin="round"/>
      <path d="m5 2 3 6H1Zm14 0-3 6h7Z" fill="#8d9ce5"/>
      <path d="m5 2 7 0-4 6Zm14 0h-7l4 6Z" fill="#f3fbff"/>
      <path d="m12 2 4 6H8Z" fill="#b1d9ff"/>
      <path d="m1 8 11 13L8 8Z" fill="#91a7ea"/>
      <path d="m23 8-11 13 4-13Z" fill="#a28ae8"/>
      <path d="m8 8 4 13 4-13Z" fill="#e3f7ff"/>
      <path d="M1 8h22M5 2l7 19 7-19" stroke="#fff" strokeOpacity=".5" strokeWidth=".5"/>
      <path className="roas-award__shine" d="m7 1 1 3 3 1-3 1-1 3-1-3-3-1 3-1Z" fill="#fff"/>
    </svg>
  ) : '🏆'}</span>;
}

export default function RoasValue({ value, children, award = false, winRoas }) {
  const display = children ?? String(value ?? '—');
  const icon = award ? <RoasAward value={value} winRoas={winRoas} /> : null;
  if (!hasDarkMatter(value)) return award ? <>{display}{icon}</> : display;
  // Leave suffix icons (e.g. the winner trophy) in their normal colors.
  const match = typeof display === 'string' && /^([+-]?\d+(?:\.\d+)?(?:x|×)?)(.*)$/s.exec(display);
  return <><span className="roas-dark-matter">{match ? match[1] : display}</span>{match ? match[2] : null}{icon}</>;
}

export function MetricValue({ label, value }) {
  if (typeof label !== 'string' || !/(?:^|[\s-])ROAS$/i.test(label) || (typeof value !== 'string' && typeof value !== 'number')) return value;
  const match = /^([+-]?\d+(?:\.\d+)?)(?:x|×)?(?:\s.*)?$/.exec(String(value));
  return match ? <RoasValue value={Number(match[1])}>{value}</RoasValue> : value;
}

// HTML text clipping inside SVG keeps the same texture on both large gauges.
// Ordinary values retain their original SVG text, geometry, and colors.
export function GaugeRoasValue({ value, children, x, y, fontSize, fill, style }) {
  if (!hasDarkMatter(value)) return <text x={x} y={y} textAnchor="middle" fontFamily="Inter,sans-serif" fontSize={fontSize} fontWeight="900" fill={fill} style={style}>{children}</text>;
  return <foreignObject x={x - 70} y={y - fontSize} width="140" height={fontSize * 1.5} overflow="visible">
    <div xmlns="http://www.w3.org/1999/xhtml" style={{ textAlign: 'center', fontFamily: 'Inter,sans-serif', fontSize, fontWeight: 900, lineHeight: 1.3 }}><RoasValue value={value}>{children}</RoasValue></div>
  </foreignObject>;
}
